import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ScopingItemsSharedService } from '@app/cem/project/scoping-items/scoping-items-shared-service';
import { ItemManagementService } from '@app/cem/project/services/item-management.service';
import { ErrorService } from '@app/common/services/error.service';
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions } from '@app/shared/aggrid/pxd-ag-grid-options';
import { IItemDto } from '@app/shared/models/dtos/item';
import { AgGridAngular } from 'ag-grid-angular';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IScopingItemGroupMapping } from '../../models/iscoping-item-group-mapping';
import { ItemManagementBrokerService } from '../../services/item-management-broker.service';

@Component({
  selector: 'app-scoping-items',
  templateUrl: './scoping-items.component.html',
  styleUrls: ['./scoping-items.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AgGridAngular,
  ],
})
export class ScopingItemsComponent implements OnInit {
  private scopingItemsSharedService = inject(ScopingItemsSharedService);
  private errorService = inject(ErrorService);
  private itemManagementService = inject(ItemManagementService);
  private broker = inject(ItemManagementBrokerService);

  unsubscribe$: Subject<boolean> = new Subject();
  public resetEventSub: Subscription;
  public loading: boolean = false;
  private gridApi;

  public iItemDtos: Array<IItemDto> = new Array<IItemDto>();
  public scopingItemGroups$: Observable<IScopingItemGroupMapping[]>;

  constructor() {
    this.loadData();

    this.resetEventSub = this.broker.ResetEvent.subscribe(s => {
      if (!s) return;
      this.resetAll();
    });
  }

  ngOnInit() {
    try {
      this.initScopingItems();
    }
    catch (error) {
      this.errorService.error("Error loading Scoping Items", error)
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
  }

  public gridOptions: PxdAgGridOptions = {
    enableCharts: true,
    cellSelection: true,
    rowGroupPanelShow: 'never',
    defaultColDef: PxdAgEditGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    columnDefs: [
      {
        field: 'ItemNumber', headerName: 'Item Number', editable: false, dndSource: true, width: 120
      },
      {
        field: 'Name', headerName: 'Item Name', editable: false
      },
      {
        field: 'ItemCategoryId', headerName: 'Item Category', editable: false,
        valueGetter: params => {
          return this.getIItemCategoryDtosById(params.data.ItemCategoryId);
        }
        , width: 150
      },
      {
        field: 'ItemSubCategoryId', headerName: 'Item Sub Category', editable: false,
        valueGetter: params => {
          return this.getIItemSubCatDtosById(params.data.ItemSubCategoryId);
        }
        , width: 120
      },
      {
        field: 'ItemScopingCategoryId', headerName: 'Item Scoping Category', editable: false,
        valueGetter: params => {
          return this.getIItemScopingCategoryDtosById(params.data.ItemScopingCategoryId);
        }
      }
    ]
  }

  initScopingItems() {
    this.scopingItemsSharedService.isItemsLoaded().pipe(takeUntil(this.unsubscribe$)).subscribe(async response => {
      if (response == true && this.gridApi !== undefined) {
        await this.loadGrid();
      }
    });
  }

  async loadGrid() {
    this.loading = true;
    this.iItemDtos = this.scopingItemsSharedService.IItemDtos.filter(s => s.FunctionId === 1 && s.ItemScopingCategoryId !== null && s.IsActive);
    this.gridApi.setGridOption('rowData', this.iItemDtos);
    this.loading = false;
  }

  getIItemCategoryDtosById = (id) => {
    if (!id) return '';
    let atype = this.scopingItemsSharedService.IItemCategoryDtos.find(a => a.ItemCategoryId == id);
    if (atype) return atype.ItemCategoryName;
    return '';
  }

  getIItemSubCatDtosById = (id) => {
    if (!id) return '';
    let atype = this.scopingItemsSharedService.IItemSubCategoryDtos.find(a => a.ItemSubCategoryId == id);
    if (atype) return atype.ItemSubCategoryName;
    return '';
  }

  getIItemScopingCategoryDtosById = (id) => {
    if (!id) return '';
    let atype = this.scopingItemsSharedService.IItemScopingCategoryDtos.find(a => a.ItemScopingCategoryId == id);
    if (atype) return atype.Description;
    return '';
  }

  async onGridReady(params) {
    this.gridApi = params.api;
  }

  public async loadData() {
    let s = await this.itemManagementService.getScopingItemGroupMappings();
    this.broker.latestScopingItemGroupMapping.next(s);
  }

  private resetAll() {
    this.loadData();
  }
}